import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'
import { apiConfig } from '@/config/aws'
import type {
  CertificatePemResult,
  Device,
  IssueDeviceInput,
  IssueDeviceResult,
} from '@/types/device'
import type {
  ComponentDef,
  LotDef,
  Machine,
  MachineColorPatch,
  MachineColors,
  MachineInput,
  MachineRevision,
} from '@/types/machine'
import type { Tablet, TabletInput } from '@/types/tablet'

const api = axios.create({
  baseURL: apiConfig.baseUrl,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const session = await fetchAuthSession()
      const token = session.tokens?.idToken?.toString()

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.error('Failed to get auth token:', error)
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // 401 はトークン audience 不一致や期限切れで発生する。
    // ハンドリングは呼び出し側 (React Query) に委ねる。
    return Promise.reject(error)
  }
)

export async function fetchDevices(): Promise<Device[]> {
  const response = await api.get<{ devices: Device[] }>('/devices')
  return response.data.devices
}

export async function issueDevice(
  input: IssueDeviceInput
): Promise<IssueDeviceResult> {
  const response = await api.post<IssueDeviceResult>('/devices', input)
  return response.data
}

export async function fetchCertificatePem(
  certificateId: string
): Promise<CertificatePemResult> {
  const response = await api.get<CertificatePemResult>(
    `/certificates/${certificateId}`
  )
  return response.data
}

export async function deactivateCertificate(
  certificateId: string
): Promise<void> {
  await api.post(`/certificates/${certificateId}/deactivate`)
}

export async function activateCertificate(
  certificateId: string
): Promise<void> {
  await api.post(`/certificates/${certificateId}/activate`)
}

export async function fetchMachines(): Promise<Machine[]> {
  const response = await api.get<{ machines: Machine[] }>('/machines')
  return response.data.machines
}

export async function createMachine(input: MachineInput): Promise<Machine> {
  const response = await api.post<Machine>('/machines', input)
  return response.data
}

export async function fetchMachine(
  machineId: string
): Promise<{ machine: Machine }> {
  const response = await api.get<{ machine: Machine }>(`/machines/${machineId}`)
  return response.data
}

export async function updateMachine(
  machineId: string,
  input: MachineInput
): Promise<Machine> {
  const response = await api.put<Machine>(`/machines/${machineId}`, input)
  return response.data
}

// 一覧の行・セルのマーカー色のみを部分更新する（リビジョンは残さない）
export async function updateMachineColors(
  machineId: string,
  patch: MachineColorPatch
): Promise<Machine> {
  const response = await api.put<Machine>(
    `/machines/${machineId}/colors`,
    patch
  )
  return response.data
}

// 台帳未登録の IoT デバイス行のマーカー色（deviceName → 色）
export async function fetchDeviceColors(): Promise<
  Record<string, MachineColors>
> {
  const response = await api.get<{
    deviceColors: Record<string, MachineColors>
  }>('/device-colors')
  return response.data.deviceColors
}

export async function updateDeviceColors(
  deviceName: string,
  patch: MachineColorPatch
): Promise<MachineColors & { deviceName: string }> {
  const response = await api.put<MachineColors & { deviceName: string }>(
    `/device-colors/${encodeURIComponent(deviceName)}`,
    patch
  )
  return response.data
}

export async function fetchRevisions(
  machineId: string
): Promise<MachineRevision[]> {
  const response = await api.get<{ revisions: MachineRevision[] }>(
    `/machines/${machineId}/revisions`
  )
  return response.data.revisions
}

export async function restoreRevision(
  machineId: string,
  revisionId: string
): Promise<Machine> {
  const response = await api.post<Machine>(
    `/machines/${machineId}/revisions/${encodeURIComponent(revisionId)}/restore`
  )
  return response.data
}

// 部品マスタ（全機材共通の構成部品リスト）
export async function fetchComponentMaster(): Promise<ComponentDef[]> {
  const response = await api.get<{ components: ComponentDef[] }>('/components')
  return response.data.components
}

// マスタを置き換え、追加・削除・改名・区分変更を全機材の構成部品へ一括反映する
// renames に指定した部品は削除+追加ではなく名前の付け替えになる（S/N・メモ維持）
export async function updateComponentMaster(
  components: ComponentDef[],
  renames: { from: string; to: string }[] = []
): Promise<{ components: ComponentDef[]; machinesUpdated: number }> {
  const response = await api.put<{
    components: ComponentDef[]
    machinesUpdated: number
  }>('/components', { components, renames })
  return response.data
}

// ロットマスタ（全機材共通のロットリスト。note に変化点などの備考を持つ）
export async function fetchLotMaster(): Promise<LotDef[]> {
  const response = await api.get<{ lots: LotDef[] }>('/lots')
  return response.data.lots
}

// マスタを置き換える。renames に指定したロットは削除+追加ではなく
// 全機材のロット表記の付け替えになる。削除は機材側の値には影響しない
export async function updateLotMaster(
  lots: LotDef[],
  renames: { from: string; to: string }[] = []
): Promise<{ lots: LotDef[]; machinesUpdated: number }> {
  const response = await api.put<{ lots: LotDef[]; machinesUpdated: number }>(
    '/lots',
    { lots, renames }
  )
  return response.data
}

// タブレット（Android タブレット等の付帯機材）。AutoFork とは linkedMachineId で紐付く
export async function fetchTablets(): Promise<Tablet[]> {
  const response = await api.get<{ tablets: Tablet[] }>('/tablets')
  return response.data.tablets
}

export async function createTablet(input: TabletInput): Promise<Tablet> {
  const response = await api.post<Tablet>('/tablets', input)
  return response.data
}

export async function updateTablet(
  tabletId: string,
  input: TabletInput
): Promise<Tablet> {
  const response = await api.put<Tablet>(`/tablets/${tabletId}`, input)
  return response.data
}

export async function deleteTablet(tabletId: string): Promise<void> {
  await api.delete(`/tablets/${tabletId}`)
}

export function apiErrorMessage(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string } | undefined
    if (data?.error) return data.error
  }
  return null
}

export default api
